import * as z from "zod";
import { IsoDate } from "./ids.js";

/**
 * The plans the product sells.
 *
 * - `free`: the workspace starts here (and the 7-day trial runs on it).
 * - `start`: one person getting started.
 * - `plus`: one person who uses it every day.
 * - `pro`: heavy use, the recommended plan.
 * - `empresa`: a team, priced per seat.
 * - `enterprise`: negotiated; the catalog has no number for it.
 */
export const PLAN_IDS = ["free", "start", "plus", "pro", "empresa", "enterprise"] as const;
export const PlanIdSchema = z.enum(PLAN_IDS);
export type PlanId = z.infer<typeof PlanIdSchema>;

/**
 * What a plan allows. A null number means "no limit of this kind"; a plan never meters
 * something it does not offer.
 */
export const PlanEntitlementsSchema = z.object({
  /** Model work included every month, in US cents of credit. */
  taskCreditsCents: z.number().int().nonnegative().nullable(),
  /** Live cloud desktops, in minutes per month. */
  cloudComputerMinutes: z.number().int().nonnegative().nullable(),
  /** How many cloud computers may run at the same time. */
  concurrentCloudComputers: z.number().int().nonnegative().nullable(),
  /** A computer on the member's own machine. Allowed on every plan. */
  localComputerAllowed: z.boolean(),
  /** Speech and calls, in minutes per month. */
  voiceMinutes: z.number().int().nonnegative().nullable(),
  /** How many agents the workspace may have. */
  maxAgents: z.number().int().nonnegative().nullable(),
  /** Child profiles a family may create (Preá Kids). Zero means the plan does not include them. */
  childProfiles: z.number().int().nonnegative().nullable().default(0),
  /** How many teammates the workspace may have. */
  seats: z.number().int().nonnegative().nullable(),
  /** Single sign-on. */
  sso: z.boolean(),
  /** The audit log. */
  audit: z.boolean(),
  /** How long a cloud computer may sit idle before it is reclaimed, in seconds. */
  computerIdleSeconds: z.number().int().nonnegative().nullable(),
  /** The longest a single computer session may run, in seconds. */
  maxComputerSessionSeconds: z.number().int().nonnegative().nullable(),
  /** CPUs, memory (MB) and process count of one cloud computer. Null means the host ceiling. */
  computerCpus: z.number().positive().nullable().default(null),
  computerMemoryMb: z.number().int().positive().nullable().default(null),
  computerPidsLimit: z.number().int().positive().nullable().default(null),
});
export type PlanEntitlements = z.infer<typeof PlanEntitlementsSchema>;

/** The launch promotion as it applies to a price or a subscription. */
export const PromoSchema = z.object({
  /** Whether new subscriptions get the promotional price today. */
  campaignOpen: z.boolean(),
  /** Last instant a new subscription still gets the promotion; null when no campaign is set. */
  campaignEndsAt: IsoDate.nullable(),
  discountPercent: z.number().int().min(0).max(100),
  /** Until when this workspace keeps the promotional price; null when it does not hold one. */
  priceHeldUntil: IsoDate.nullable(),
});
export type Promo = z.infer<typeof PromoSchema>;

/**
 * One plan in the catalog. The prices are what the operator configured, in BRL centavos;
 * they are never a constant in the code.
 */
export const PlanSchema = z.object({
  id: PlanIdSchema,
  name: z.string().min(1),
  /** A short line the marketing page and the checkout show. */
  summary: z.string().min(1),
  /** The monthly price, in BRL centavos. Null when the plan is free or negotiated. */
  priceMonthlyCents: z.number().int().nonnegative().nullable(),
  /** The annual price, in BRL centavos. Null when the plan has no annual price. */
  priceAnnualCents: z.number().int().nonnegative().nullable(),
  /** The launch price while the campaign is open; null when the plan has none. */
  promoPriceMonthlyCents: z.number().int().nonnegative().nullable().default(null),
  promoPriceAnnualCents: z.number().int().nonnegative().nullable().default(null),
  /** The price is per seat. */
  perSeat: z.boolean(),
  /** The price is negotiated; the catalog has no number. */
  custom: z.boolean(),
  entitlements: PlanEntitlementsSchema,
});
export type Plan = z.infer<typeof PlanSchema>;

/** Every plan the deployment sells. */
export const PlanCatalogSchema = z.object({
  plans: z.array(PlanSchema),
});
export type PlanCatalog = z.infer<typeof PlanCatalogSchema>;

/**
 * The plan and entitlements in force for a workspace. The source says where they came from:
 * the workspace's own plan, an installed license, the operator's configuration, or the free
 * plan for a workspace that never chose one.
 */
export const PlanStateSchema = z.object({
  planId: PlanIdSchema,
  name: z.string().min(1),
  entitlements: PlanEntitlementsSchema,
  priceMonthlyCents: z.number().int().nonnegative().nullable(),
  priceAnnualCents: z.number().int().nonnegative().nullable(),
  promoPriceMonthlyCents: z.number().int().nonnegative().nullable().default(null),
  promoPriceAnnualCents: z.number().int().nonnegative().nullable().default(null),
  /** The promotion: campaign window and, for a subscriber, how long the price is held. */
  promo: PromoSchema.nullable().default(null),
  perSeat: z.boolean(),
  custom: z.boolean(),
  /** The seats the workspace is using. */
  seats: z.number().int().nonnegative().nullable(),
  source: z.enum(["plan", "license", "operator", "free"]),
});
export type PlanState = z.infer<typeof PlanStateSchema>;

/** The edition of the installable product a license is for. */
export const LicenseEditionSchema = z.enum(["pessoal", "empresa"]);
export type LicenseEdition = z.infer<typeof LicenseEditionSchema>;

/** The features a license can turn on. */
export const LICENSE_FEATURE_IDS = [
  "sso",
  "audit",
  "local_computer",
  "cloud_computers",
  "priority_support",
] as const;
export type LicenseFeatureId = (typeof LICENSE_FEATURE_IDS)[number];

/**
 * What a license says. The file is signed, so every field here is Orbin's word, not the
 * operator's.
 */
export const LicensePayloadSchema = z.object({
  licenseId: z.string().min(8),
  licensee: z.string().min(1),
  edition: LicenseEditionSchema,
  /** How many teammates the license covers. */
  seats: z.number().int().positive(),
  /** How many agents the license allows. */
  maxAgents: z.number().int().positive(),
  /** The features that are on. */
  features: z.array(z.string().min(1)),
  issuedAt: IsoDate,
  expiresAt: IsoDate,
});
export type LicensePayload = z.infer<typeof LicensePayloadSchema>;

/** The license file: the signed payload and its signature. */
export const LicenseFileSchema = z.object({
  payload: LicensePayloadSchema,
  /** The Ed25519 signature, base64. */
  signature: z.string().min(1),
});
export type LicenseFile = z.infer<typeof LicenseFileSchema>;

/**
 * The state of the installed license.
 *
 * - `valid`: in force.
 * - `grace`: expired, but the workspace still has its grace period.
 * - `read_only`: the grace period is over. New work is refused; the data stays readable
 *   and exportable.
 * - `expired`: expired with no grace period configured.
 * - `invalid`: the file does not verify.
 * - `missing`: no license is installed.
 */
export const LicenseStateSchema = z.enum([
  "valid",
  "grace",
  "read_only",
  "expired",
  "invalid",
  "missing",
]);
export type LicenseState = z.infer<typeof LicenseStateSchema>;

/** What the admin view shows about the installed license. */
export const LicenseStatusSchema = z.object({
  state: LicenseStateSchema,
  licenseId: z.string().nullable(),
  licensee: z.string().nullable(),
  edition: LicenseEditionSchema.nullable(),
  seats: z.number().int().nonnegative().nullable(),
  maxAgents: z.number().int().nonnegative().nullable(),
  features: z.array(z.string()),
  issuedAt: IsoDate.nullable(),
  expiresAt: IsoDate.nullable(),
  /** When the grace period ends, after the license expired. */
  graceEndsAt: IsoDate.nullable(),
  /** Days left before the state changes. */
  daysRemaining: z.number().int().nullable(),
  /** Why a license was refused, when it was. */
  reason: z.string().nullable(),
});
export type LicenseStatus = z.infer<typeof LicenseStatusSchema>;

/** The code a refusal carries, so a client can tell one from another. */
export const LICENSE_READ_ONLY_CODE = "license_read_only";
export const LICENSE_SEATS_EXCEEDED_CODE = "license_seats_exceeded";
export const LICENSE_AGENTS_EXCEEDED_CODE = "license_agents_exceeded";
/** A workspace on a plan may not create a Preá past the plan's maxAgents. */
export const PLAN_AGENTS_EXCEEDED_CODE = "plan_agents_exceeded";

/** How long a license keeps working after it expires, in days. */
export const LICENSE_GRACE_DAYS = 14;
